// Reducer and State which will control the application Routing function
import { Action, ActionCreator, combineReducers, Reducer } from 'redux';
import acquireHealthCardNumberReducer from './AcquireHealthCardNumber/reducer';
import { AcquireHealthCardNumberState, ACQUIRING_MODE } from './AcquireHealthCardNumber/type';
import adminOptionsReducer from './AdminOptions/reducer';
import { AdminOptionsState } from './AdminOptions/type';
import appointmentReducer from './Appointment/reducer';
import { AppointmentState } from './Appointment/type';
import clinicInformationReducer from './ClinicInformation/reducer';
import { ClinicInformationState } from './ClinicInformation/type';
import errorsReducer from './Errors/reducer';
import { ErrorsState } from './Errors/type';
import keyboardReducer from './Keyboard/reducer';
import { KeyboardState } from './Keyboard/type';
import languageLocaleReducer from './LanguageLocale/reducer';
import loadingSpinnerReducer from './LoadingSpinner/reducer';
// Import your state types and reducers here.
import { LocaleState } from './LanguageLocale/type';
import loginReducer from './Login/reducer';
import { LoginState } from './Login/type';
import patientInformationFormReducer from './PatientInformationForm/reducer';
import { PatientInformationFormState } from './PatientInformationForm/type';
import snackBarPopUpReducer from './SnackBarPopup/reducer';
import { SnackBarPopupState } from './SnackBarPopup/type';
import utilitiesReducer from './Utilities/reducer';
import { UtilitesState } from './Utilities/type';
import { LoadingSpinnerState } from './LoadingSpinner/type';
import { SurveyState } from './Survey/type';
import surveyReducer from './Survey/reducer';
import routingReducer from './Routing/reducer';
import { RoutingState } from './Routing/types';
// The top-level state object
export interface ApplicationState {
    localeText: LocaleState,
    acquireHealthCardNumber: AcquireHealthCardNumberState,
    appointment: AppointmentState,
    patientInformationForm: PatientInformationFormState,
    errors: ErrorsState,
    utilities: UtilitesState,
    routing: RoutingState,
    login: LoginState,
    adminOptions: AdminOptionsState,
    clinicInformation: ClinicInformationState,
    keyboard: KeyboardState,
    snackBarPopUp: SnackBarPopupState,
    loadingSpinner: LoadingSpinnerState,
    survey: SurveyState
}

/// Top level application store containing all reducers
export const appReducer: Reducer<ApplicationState> = combineReducers<ApplicationState>({
    localeText: languageLocaleReducer,
    acquireHealthCardNumber: acquireHealthCardNumberReducer,
    appointment: appointmentReducer,
    patientInformationForm: patientInformationFormReducer,
    errors: errorsReducer,
    utilities: utilitiesReducer,
    login: loginReducer,
    adminOptions: adminOptionsReducer,
    clinicInformation: clinicInformationReducer,
    keyboard: keyboardReducer,
    snackBarPopUp: snackBarPopUpReducer,
    loadingSpinner: loadingSpinnerReducer,
    survey: surveyReducer,
    routing: routingReducer
});

/**
 * RESET STORE CONSTANTS, ACTION and HANDLERS
 */
export const RESET_ALL_STORES = 'RESET_ALL_STORES';
export type RESET_ALL_STORES = typeof RESET_ALL_STORES;

export const ERASE_ALL_DATA = 'ERASE_ALL_DATA';
export type ERASE_ALL_DATA = typeof ERASE_ALL_DATA;

// Reset 
export interface ResetAllStoresAction extends Action {
    type: RESET_ALL_STORES;
}

export const resetAllStores: ActionCreator<ResetAllStoresAction> = () => ({
    type: RESET_ALL_STORES
});

export interface EraseAllStoreDataAction extends Action {
    type: ERASE_ALL_DATA;
}

export const eraseAllStoreData: ActionCreator<EraseAllStoreDataAction> = () => ({
    type: ERASE_ALL_DATA
});

/**
 * @description Resets everything to default except adminOptions, authentication status and default clinic data.
 * @param {ApplicationState} state
 * @param {ResetAllStoresAction} action
 * @returns {ApplicationState}
 */
function resetAllStoresHandlers(state: ApplicationState, action: ResetAllStoresAction): ApplicationState {
    // Keep info we want.
    const adminOptions = Object.assign({}, state.adminOptions);
    const isAuthenticated: boolean = state.login.isAuthenticated;
    const defaultClinicData = Object.assign({}, state.clinicInformation);
    const localeText = Object.assign({}, state.localeText);
    const currentRoute = state.routing.currentRoute;
    const debugMessages = state.utilities.messages;
    const currentMaskCountry = state.acquireHealthCardNumber.maskCountry;
    const currentMaskRegion = state.acquireHealthCardNumber.maskRegion;
    state = undefined;
    // recreate initial app state
    const newState = Object.assign({}, appReducer(state, action));
    // apply kept info to new state

    newState.routing.currentRoute = currentRoute;
    newState.localeText = localeText;
    newState.localeText.currentLanguageText =
        newState.localeText.textLibrary.setAndGetCurrentLanguageResource(adminOptions.defaultLanguage);

    newState.localeText.showLanguageSelection = true;
    newState.utilities.messages = debugMessages;
    newState.login.isAuthenticated = isAuthenticated;
    newState.clinicInformation = defaultClinicData;
    newState.adminOptions = adminOptions;
    newState.localeText.lang = adminOptions.defaultLanguage;
    newState.acquireHealthCardNumber.acquiringMode = ACQUIRING_MODE.SCANNER;
    newState.acquireHealthCardNumber.maskRegion = currentMaskRegion;
    newState.acquireHealthCardNumber.maskCountry = currentMaskCountry;
    return newState;
}

/**
 * @description The first or root reducer. In debug mode, all actions and their payload will be sent to the console.
 *              Will also intercept the RESET_ALL_STORES dispatch.
 * @param {ApplicationState} state 
 * @param {Action} action 
 */
export const rootReducer = (state: ApplicationState, action: Action) => {
    if (action.type === RESET_ALL_STORES) {
        return resetAllStoresHandlers(state, action);
    } else if (action.type === ERASE_ALL_DATA) {
        return appReducer(undefined, { type: ERASE_ALL_DATA });
    }
    if (state !== undefined) {
        if (state.utilities.isDebugModeOn) {
            // tslint:disable-next-line: no-console
            console.log(action.type);
        }
    }
    return appReducer(state, action);
}






// WEBPACK FOOTER //
// ./src/store/index.ts